import React, {
  useRef,
  useMemo,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import clsx from 'clsx';
import { PhonicsSound, useGetPhonicsSoundsQuery } from '@/api/types';
import { useUi, useAssetCdn } from '@/context/Ui';
import { useLessonPlayer } from '@/context/LessonPlayer';
import { findSoundsInString } from '@/utils/sounds';
import { SymbolType, CdnObjectType } from '@/types';
import { AudioPlayer, CdnMedia, Robot } from '@/components';
import { WhitePanel } from '../components/WhitePanel';
import { ActivityTitleStep } from '../components/ActivityTitleStep';
import { Activity, ActivityStep } from '../Activity';
import { ActivityTypeProps } from '../types';

interface RobotWrapperProps {
  total: number;
}

export interface RobotWrapper {
  handleNewIndex: (index: number) => void;
}

const RobotWrapper = forwardRef<RobotWrapper, RobotWrapperProps>(
  ({ total }, ref) => {
    const [playing, setPlaying] = useState(false);
    const [index, setIndex] = useState(-1);

    useImperativeHandle(ref, () => ({
      handleNewIndex(index: number) {
        setIndex(index);
        setPlaying(index !== -1);
      },
    }));

    const robotType = useMemo(() => {
      if (!playing || index === total - 1) {
        return 'still';
      }

      return index % 2 === 0 ? 'left' : 'right';
    }, [playing, index, total]);

    return <Robot className="w-96 mx-auto" type={robotType} />;
  },
);

RobotWrapper.displayName = 'RobotWrapper';

interface OralBlendingStepProps extends ActivityTypeProps {
  index: number;
  total: number;
  word: string;
  sounds: Array<PhonicsSound>;
}

const OralBlendingStep = ({
  activity,
  mode,
  wrapperClassName,
  isPreview,
  index,
  total,
  word,
  sounds,
}: OralBlendingStepProps) => {
  const playerRef = useRef<AudioPlayer>(null);

  const robotRef = useRef<RobotWrapper>(null);

  const { enablePlayButton, disablePlayButton } = useLessonPlayer();

  const { sendAlert } = useUi();

  const [playingIndex, setPlayingIndex] = useState<number>(-1);

  const [playedOnce, setPlayedOnce] = useState(false);

  const [playerReady, setPlayerReady] = useState(false);

  const [showImage, setShowImage] = useState(false);

  const wordSounds = useMemo(
    () =>
      findSoundsInString(
        sounds.map((sound) => sound.name),
        word,
      ),
    [word, sounds],
  );

  const soundsMediaUrls = useAssetCdn(
    wordSounds,
    SymbolType.Sound,
    CdnObjectType.Audio,
  );

  const wordMediaUrl = useAssetCdn(word, SymbolType.Word, CdnObjectType.Audio);

  const mediaUrls = useMemo(() => {
    if (Array.isArray(soundsMediaUrls)) {
      return [...soundsMediaUrls, wordMediaUrl];
    }

    if (!!soundsMediaUrls) {
      return [soundsMediaUrls, wordMediaUrl];
    }

    return [];
  }, [soundsMediaUrls, wordMediaUrl]);

  const isPenultimateSound = useMemo(
    () => playingIndex < mediaUrls.length - 2,
    [playingIndex, mediaUrls],
  );

  return (
    <ActivityStep
      stepIdentifier={`${activity.id}-${index}-1`}
      activity={activity}
      mode={mode}
      stepNumber={index + 1}
      totalSteps={total}
      wrapperClassName={wrapperClassName}
      isPreview={isPreview}
      usePanel
      title={`Hear and blend the sounds. Say the word!`}
      showTitle={true}
      showActivityPagination={false}
      onSlide={() => {
        enablePlayButton({
          onClick: () => {
            playerRef.current?.play();
            setPlayedOnce(true);
          },
          disabled: !playerReady,
          hint: !playedOnce,
        });
      }}
      onBeforeLeave={() => {
        disablePlayButton();

        return true;
      }}
    >
      <WhitePanel mode="basic" kind="fill" color="paper" gap={true}>
        <div className="flex flex-col sm:flex-row xl:flex-col gap-3 mx:gap-6 items-center">
          <RobotWrapper ref={robotRef} total={mediaUrls.length} />

          <div className="w-auto sm:w-1/2 xl:w-auto">
            <CdnMedia
              className={clsx(
                'block mx-auto w-40 h-auto md:w-64 opacity-0',
                'transition-opacity duration-200',
                showImage && 'opacity-100',
              )}
              symbol={word}
              symbolType={SymbolType.Word}
              objectType={CdnObjectType.Image}
              width={200}
              height={200}
            />

            <h3
              className={clsx(
                'text-6xl font-sassoonInfant opacity-0',
                'transition-opacity duration-200',
                showImage && 'opacity-100',
              )}
            >
              {word}
            </h3>
          </div>
        </div>
      </WhitePanel>

      <AudioPlayer
        ref={playerRef}
        src={mediaUrls as Array<string>}
        pause={isPenultimateSound ? 0 : 1000}
        onPlaySrc={(src, index) => {
          console.log('Playing sound:', src, 'at index:', index);
          robotRef.current?.handleNewIndex(index);
          setPlayingIndex(index);
        }}
        onEnded={() => {
          setPlayingIndex(-1);
          robotRef.current?.handleNewIndex(-1);
          setShowImage(true);
        }}
        onReadySrc={() => setPlayerReady(true)}
        onLoadTimeoutSrc={() =>
          sendAlert('We could not load some of the audio for this lesson.', {
            type: 'warning',
            title: 'There was a problem',
          })
        }
      />
    </ActivityStep>
  );
};

export const ReadingOralBlending = ({
  activity,
  mode,
  wrapperClassName,
  isPreview,
}: ActivityTypeProps) => {
  if (!activity.sounds) {
    //throw new Error('Sounds are required for this activity');
  }

  if (!activity.words) {
    throw new Error('Words are required for this activity');
  }

  const { data: soundsData } = useGetPhonicsSoundsQuery();

  const { words } = activity;

  const NUMBER_OF_STEPS_PER_WORD = 1;
  const TOTAL_STEPS = 1 + NUMBER_OF_STEPS_PER_WORD * words.length;

  return (
    <Activity
      steps={[
        <ActivityTitleStep
          key={`${activity.id}-title`}
          activity={activity}
          mode={mode}
          wrapperClassName={wrapperClassName}
          parrotType="robot"
          isPreview={isPreview}
          index={0}
          total={words.length * NUMBER_OF_STEPS_PER_WORD + 1}
        />,
        ...words.flatMap((word, index) => [
          <OralBlendingStep
            key={`${activity.id}-${index}-2`}
            activity={activity}
            mode={mode}
            isPreview={isPreview}
            wrapperClassName={wrapperClassName}
            index={index + 1}
            total={TOTAL_STEPS}
            word={word}
            sounds={soundsData?.getPhonicsSounds || []}
          />,
        ]),
      ]}
    />
  );
};
